import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../../api/axiosInstance';
import { ModalAddCategory } from '../modalAddCategory';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Loader } from '../../../components/loader';

interface Category {
  id: number;
  name: string;
  parentId: number | null;
}

interface ModalChooseCategoryProps {
  openCategory: boolean;
  handleCloseCategory: () => void;
  type: 'income' | 'expense';
  onSelectCategory: (id: string, label: string) => void;
}

type ToastState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
};

export const ModalChooseCategory: React.FC<ModalChooseCategoryProps> = ({
  openCategory,
  handleCloseCategory,
  type,
  onSelectCategory,
}) => {
  const queryClient = useQueryClient();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const [openAddCategory, setOpenAddCategory] = useState(false);

  const [editMode, setEditMode] = useState<false | 'edit'>(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);

  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const endpointBase = type === 'income' ? '/categories-income' : '/categories-expense';

  const {
    data: categories = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<Category[], Error>({
    queryKey: ['categories', type],
    queryFn: async () => {
      const { data } = await api.get<Category[]>(endpointBase);
      return data;
    },
    enabled: openCategory,
    staleTime: 30_000,
  });

  const deleteMutation = useMutation<void, Error, Category>({
    mutationFn: async cat => {
      await api.delete(`${endpointBase}/${cat.id}`);
    },
    onSuccess: async (_data, cat) => {
      setToast({ open: true, message: 'Категория удалена', severity: 'success' });

      if (selectedId === String(cat.id)) {
        setSelectedId(null);
        setSelectedLabel('');
      }

      queryClient.invalidateQueries({ queryKey: ['categories', type] });
      await refetch();
    },
    onError: (e: any) => {
      const msg = isAxiosError(e)
        ? (e.response?.data?.message ?? e.message)
        : 'Не удалось удалить категорию. Убедитесь, что нет дочерних или связанных операций.';
      setToast({ open: true, message: msg, severity: 'error' });
    },
  });

  const buildTree = (
    items: Category[],
  ): Array<Category & { children: Array<Category & { children: any[] }> }> => {
    const map: Record<number, Category & { children: Category[] }> = {};
    items.forEach(item => {
      map[item.id] = { ...item, children: [] };
    });

    const roots: Array<Category & { children: Category[] }> = [];
    items.forEach(item => {
      if (item.parentId != null && map[item.parentId]) {
        map[item.parentId].children.push(map[item.id]);
      } else {
        roots.push(map[item.id]);
      }
    });

    return roots as Array<Category & { children: Array<Category & { children: any[] }> }>;
  };

  const treeData = useMemo(() => buildTree(categories), [categories]);

  // чтобы не разворачивать ВСЁ дерево (это тяжело и на мобилке может тормозить),
  // лучше раскрыть только верхний уровень:
  const defaultExpanded = useMemo(() => treeData.map(n => String(n.id)), [treeData]);

  const handleSelect = (_: React.SyntheticEvent, itemId: string | null) => {
    if (itemId === null) {
      setSelectedId(null);
      setSelectedLabel('');
      return;
    }
    setSelectedId(itemId);
    const found = categories.find(c => c.id === Number(itemId));
    setSelectedLabel(found ? found.name : '');
  };

  const handleChoose = () => {
    if (!selectedId) return;
    onSelectCategory(selectedId, selectedLabel);
    handleCloseCategory();
  };

  const onEdit = (cat: Category) => {
    setEditingCategory(cat);
    setEditMode('edit');
    setOpenAddCategory(true);
  };

  const onDelete = async (cat: Category) => {
    const ok = window.confirm(
      `Удалить категорию "${cat.name}"? Если у неё есть дочерние категории или операции, удаление может быть отклонено.`,
    );
    if (!ok) return;
    if (deleteMutation.isPending) return;
    deleteMutation.mutate(cat);
  };

  const renderLabel = (node: Category) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
      <Typography component="span" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {node.name}
      </Typography>

      <Tooltip title="Редактировать">
        <IconButton
          size="small"
          disabled={deleteMutation.isPending}
          onClick={e => {
            e.stopPropagation();
            onEdit(node);
          }}
        >
          <EditIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Удалить">
        <IconButton
          size="small"
          color="error"
          disabled={deleteMutation.isPending}
          onClick={e => {
            e.stopPropagation();
            onDelete(node);
          }}
        >
          <DeleteIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderTree = (
    nodes: Array<Category & { children: Array<Category & { children: any[] }> }>,
  ) =>
    nodes.map(node => (
      <TreeItem key={node.id} itemId={String(node.id)} label={renderLabel(node)}>
        {node.children.length > 0 && renderTree(node.children)}
      </TreeItem>
    ));

  const closeAddModal = () => {
    setOpenAddCategory(false);
    setEditingCategory(null);
    setEditMode(false);
  };

  const afterCategoryChanged = async () => {
    closeAddModal();
    queryClient.invalidateQueries({ queryKey: ['categories', type] });
    await refetch();
  };

  const locked = isLoading || deleteMutation.isPending;

  return (
    <>
      <Dialog
        open={openCategory}
        onClose={() => !locked && handleCloseCategory()}
        fullWidth
        maxWidth="sm"
        fullScreen={false}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 560 },
            maxWidth: 560,
            m: { xs: 1, sm: 2 },
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Выбрать категорию</Typography>

          <Button
            variant="text"
            sx={{ fontSize: '40px', minWidth: 0, lineHeight: 1 }}
            disabled={locked}
            onClick={() => {
              setEditingCategory(null);
              setEditMode(false);
              setOpenAddCategory(true);
            }}
          >
            +
          </Button>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            // вот тут скролл
            maxHeight: { xs: '70vh', sm: '60vh' },
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            touchAction: 'pan-y',
            px: 1.5,
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <Loader />
            </Box>
          ) : isError ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                py: 4,
              }}
            >
              <Typography color="error">Ошибка загрузки категорий</Typography>
              <Button variant="outlined" onClick={() => refetch()}>
                Повторить
              </Button>
            </Box>
          ) : (
            <SimpleTreeView
              selectedItems={selectedId ?? undefined}
              onSelectedItemsChange={handleSelect}
              defaultExpandedItems={defaultExpanded}
              slots={{
                collapseIcon: ExpandMoreIcon,
                expandIcon: ChevronRightIcon,
              }}
              sx={{
                '& .MuiTreeItem-content': { py: 0.25 },
                '& .MuiTreeItem-label': { fontSize: { xs: 14, sm: 14 } },
              }}
            >
              {renderTree(treeData)}
            </SimpleTreeView>
          )}

          {selectedLabel ? (
            <Box sx={{ textAlign: 'center', mt: 1.5 }}>
              <Typography variant="body2">Выбрано: {selectedLabel}</Typography>
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Box sx={{ width: '100%' }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleChoose}
              disabled={!selectedId || locked}
              sx={{ mb: 1 }}
            >
              Выбрать
            </Button>

            <Button variant="outlined" fullWidth onClick={handleCloseCategory} disabled={locked}>
              Назад
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <ModalAddCategory
        open={openAddCategory}
        onClose={closeAddModal}
        type={type}
        mode={editMode ? 'edit' : 'create'}
        initial={
          editingCategory
            ? {
                id: editingCategory.id,
                name: editingCategory.name,
                parentId: editingCategory.parentId,
              }
            : undefined
        }
        onAdded={afterCategoryChanged}
        onUpdated={afterCategoryChanged}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};
